import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { HydrationDetailsService } from '../../hydration-service/hydration-details.service';
import {
  HydrationHistoryResponse,
  HydrationHistoryItem,
} from '../../hydration-const/hydration-details-models';
import { TimePeriodFilter } from '../../../assesment-detail/assesment-const/assessment-details-models';
import { ChartModule } from 'primeng/chart';

@Component({
  selector: 'patient-hydration-history-graph',
  standalone: true,
  imports: [CommonModule, ChartModule, DropdownModule, FormsModule],
  templateUrl: './hydration-history-graph.component.html',
  styleUrl: './hydration-history-graph.component.css',
})
export class HydrationHistoryGraphComponent implements OnInit {
  private authService = inject(AuthService);
  private hydrationService = inject(HydrationDetailsService);

  patientId!: string;
  loading: boolean = true;
  hydrationResponse: HydrationHistoryResponse | null = null;
  historyData: HydrationHistoryItem[] = [];

  // Summary Metrics
  todaysIntake: number = 0;
  todaysGoal: number = 0;

  // Chart configuration
  chartData: any;
  chartOptions: any;

  // Filter
  filterOptions = [
    { label: 'Daily', value: TimePeriodFilter.Weekly }, // Simulates Daily bounds per 7 day grouping
    { label: 'Weekly', value: TimePeriodFilter.Monthly },
    { label: 'Monthly', value: TimePeriodFilter.Yearly },
  ];
  selectedFilter: TimePeriodFilter = TimePeriodFilter.Weekly;

  ngOnInit() {
    this.authService.getUserProfile().subscribe((res) => {
      if (res?.PatientId) {
        this.patientId = res.PatientId;
        this.fetchGraphData();
      }
    });
  }

  fetchGraphData() {
    this.loading = true;
    this.hydrationService.getHydrationHistory(this.patientId, this.selectedFilter).subscribe({
      next: (response) => {
        if (response && response.status === 200) {
          this.hydrationResponse = response;
          this.historyData = response.history ? [...response.history].reverse() : [];
          this.todaysIntake = response.todays_intake || 0;
          this.todaysGoal = response.todays_goal || 2700;

          this.initChart();
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load hydration history:', err);
        this.loading = false;
      },
    });
  }

  onFilterChange() {
    this.fetchGraphData();
  }

  formatLiters(ml: number): number {
    return Number((ml / 1000).toFixed(1));
  }

  getHintText(): string {
    if (this.todaysIntake >= this.todaysGoal) {
      return 'Great job! You have reached your hydration goal for today.';
    } else {
      return "Your water intake is below today's goal. Try to drink water regularly throughout the day.";
    }
  }

  private initChart() {
    if (this.historyData.length === 0) return;

    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color') || '#6b7280';
    const textColorSecondary =
      documentStyle.getPropertyValue('--text-color-secondary') || '#9ca3af';
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border') || '#f3f4f6';

    const labels = this.historyData.map((item) => this.formatDayLabel(item.date));
    const intakeValues = this.historyData.map((item) => this.formatLiters(item.value));
    const goalValues = this.historyData.map((item) => this.formatLiters(item.goal)); // target line array

    // Determine colors for each bar relative to current threshold logic.
    // E.g., highest value gets specific highlight, or everything is light blue except today.
    // Since "today" is usually the last item dynamically.
    const barColors = intakeValues.map((val, index) => {
      return index === intakeValues.length - 1 ? '#3B82F6' : '#DBEAFE';
    });

    // Explicit array for hover, making ALL bars turn dark blue on hover
    const hoverColors = intakeValues.map(() => '#3B82F6');

    this.chartData = {
      labels: labels,
      datasets: [
        {
          type: 'line',
          label: 'Goal',
          data: goalValues,
          borderColor: '#4368B1', // matching dashed goal line from mockup
          borderWidth: 2,
          borderDash: [5, 5],
          fill: false,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 0,
        },
        {
          type: 'bar',
          label: 'Intake (L)',
          backgroundColor: barColors,
          hoverBackgroundColor: hoverColors,
          data: intakeValues,
          borderRadius: { topLeft: 12, topRight: 12, bottomLeft: 0, bottomRight: 0 },
          borderSkipped: false,
          categoryPercentage: 0.9,
          barPercentage: 0.9,
          maxBarThickness: 55,
        },
      ],
    };

    this.chartOptions = {
      maintainAspectRatio: false,
      aspectRatio: 2, // Flattens graph making it shorter and wider
      layout: {
        padding: {
          top: 10, // pad top so line dot doesn't get clipped
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          titleColor: '#1f2937',
          titleFont: { size: 13, weight: 'bold' },
          bodyColor: '#4b5563',
          bodyFont: { size: 14, weight: 'bold' },
          padding: 12,
          cornerRadius: 8,
          borderColor: '#e5e7eb',
          borderWidth: 1,
          displayColors: false,
          callbacks: {
            label: (context: any) => {
              if (context.dataset.type === 'line') return ''; // ignore goal tooltip
              return `${context.parsed.y}L`;
            },
            title: () => '', // Hide title to just show the value like in mockup 4.6L bubble
          },
        },
      },
      scales: {
        x: {
          grid: {
            color: 'transparent',
            drawBorder: false,
          },
          ticks: {
            color: '#9ca3af',
            font: { size: 12, weight: '500' },
          },
        },
        y: {
          display: false,
          beginAtZero: true,
          suggestedMax: Math.max(...goalValues) * 1.3, // Push graph down slightly leaving room for line
        },
      },
      interaction: {
        intersect: false,
        mode: 'index',
      },
    };
  }

  private formatDayLabel(dateStr: string): string {
    if (!dateStr || dateStr.length < 10) return dateStr;
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const date = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return days[date.getDay()];
    }
    return dateStr;
  }
}
